import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { CollectedAt, GradeChip, Pill, label } from './components/primitives'
import { listItems, setVeracity, type Item } from './api'

/**
 * The triage queue — unverified claims, oldest custody first.
 *
 * ## Corroborating and disputing both ask for the other item
 *
 * "Somebody disagrees" without saying who is not a corroboration record. The server accepts a
 * `relatedItemId` for exactly that reason, and the buttons here that need one say so rather
 * than silently writing a bare status.
 *
 * Note what triage does NOT change: the Admiralty grade. That records what the item was worth
 * when it was collected. "Turned out to be true" is the veracity column's job, and
 * overwriting the grade would erase the difference between a claim that was always credible
 * and one that got lucky.
 */
export function TriagePage() {
  const [items, setItems] = useState<Item[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const page = await listItems({ veracityCode: 'UNVERIFIED', size: '100' })
      // Oldest custody first: a claim that has sat unverified for a month is the one that
      // most needs somebody to look at it.
      setItems([...page.content].sort((a, b) => a.collectedAt.localeCompare(b.collectedAt)))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function decide(item: Item, veracityCode: string) {
    setBusy(item.id)
    try {
      await setVeracity(item.id, { veracityCode })
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  if (error && !items) return <ErrorStrip message={error} />
  if (!items) return <LoadingBlock label="Loading the queue" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Triage queue"
        description="Unverified claims, oldest custody first. Triage moves the VERACITY — the
                     Admiralty grade records what the item was worth when it arrived and does
                     not change."
      />
      {error && <ErrorStrip message={error} />}
      <Panel>
        <ErpTable head={<>
          <Th>Grade</Th>
          <Th>Item</Th>
          <Th className="axis-hide-sm">Type</Th>
          <Th>Collected</Th>
          <Th>Decide</Th>
        </>}>
          {items.length === 0 && (
            <EmptyRow colSpan={5} message="Nothing waiting. Every claim on file has been looked at." />
          )}
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-muted/40">
              <Td><GradeChip grade={item.grading} /></Td>
              <Td>
                <span>{item.redacted ? '[redacted]' : item.title ?? '(untitled)'}</span>
                {item.attestationText && (
                  <span className="block text-[10px] text-muted-foreground">
                    Attested: {item.attestationText}
                  </span>
                )}
              </Td>
              <Td className="axis-hide-sm">
                <Pill className="border-border">{label(item.itemTypeCode)}</Pill>
              </Td>
              <Td><CollectedAt at={item.collectedAt} /></Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {['VERIFIED', 'CORROBORATED', 'DISPUTED', 'DEBUNKED'].map((v) => (
                    <button key={v} type="button" disabled={busy === item.id}
                            onClick={() => void decide(item, v)}
                            className="rounded border border-border px-1.5 py-0.5 text-[11px]">
                      {label(v)}
                    </button>
                  ))}
                </div>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
